import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { loadAdminProfile, setAdminUiLocale } from "@/lib/admin.client";
import { adminStrings, ADMIN_LOCALE_OPTIONS, type AdminLang } from "@/lib/admin-i18n";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { Queue } from "@/components/admin/Queue";
import { StoryDetail } from "@/components/admin/StoryDetail";

// §3: /admin is locale-free; per-account ui_locale lives on profiles.
// §7: staff-only — Supabase Auth + RLS via has_role().
export const Route = createFileRoute("/admin")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Admin — MadCaptains" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminPage,
});

function AdminPage() {
  const [userId, setUserId] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data }) => {
      if (!mounted) return;
      setUserId(data.user?.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  if (userId === undefined) {
    return <div className="mx-auto max-w-sm px-6 py-20 text-sm text-muted-foreground">…</div>;
  }

  if (userId === null) {
    // Brief default-EN view until signed in; once signed in we load ui_locale.
    const t = adminStrings("en");
    return (
      <div className="min-h-screen" style={{ background: "var(--paper)" }}>
        <AdminLogin t={t} onSignedIn={() => { /* state set by listener */ }} />
      </div>
    );
  }

  return <AdminAuthedShell />;
}

function AdminAuthedShell() {
  const qc = useQueryClient();
  const { data: profile, isLoading, error } = useQuery({
    queryKey: ["admin", "profile"],
    queryFn: () => loadAdminProfile(),
  });

  const [openId, setOpenId] = useState<string | null>(null);

  if (isLoading) return <div className="mx-auto max-w-sm px-6 py-20 text-sm">…</div>;

  const lang: AdminLang = (profile?.ui_locale as AdminLang) ?? "en";
  const t = adminStrings(lang);

  async function signOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
  }

  async function changeLocale(next: AdminLang) {
    await setAdminUiLocale({ data: { ui_locale: next } });
    qc.invalidateQueries({ queryKey: ["admin", "profile"] });
  }

  if (error) {
    return (
      <div className="mx-auto max-w-md px-6 py-16">
        <p className="text-sm" style={{ color: "var(--rust)" }}>{(error as Error).message}</p>
        <button onClick={signOut} className="mt-4 text-sm underline">{t.sign_out}</button>
      </div>
    );
  }

  const noRole = (profile?.roles?.length ?? 0) === 0;

  return (
    <div className="min-h-screen" style={{ background: "var(--paper)" }}>
      <header
        className="border-b"
        style={{ borderColor: "var(--fog)", background: "color-mix(in oklab, var(--paper) 90%, var(--navy) 10%)" }}
      >
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-xs underline text-muted-foreground hover:text-foreground"
              title={lang === "ru" ? "На главную" : "Back to site"}
            >
              ← {lang === "ru" ? "На главную" : "Home"}
            </Link>
            <span className="font-display text-base" style={{ color: "var(--ink)" }}>
              MadCaptains <span className="text-muted-foreground">— Bridge</span>
            </span>
            <span className="ml-3 text-xs text-muted-foreground">
              {t.signed_in_as} {profile?.email}
              {profile?.roles && profile.roles.length > 0 && (
                <> · {profile.roles.join(", ")}</>
              )}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              {t.ui_locale}:
              <select
                value={lang}
                onChange={(e) => changeLocale(e.target.value as AdminLang)}
                className="rounded-sm border border-border bg-background px-2 py-1 text-xs"
              >
                {ADMIN_LOCALE_OPTIONS.map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.label}{o.ready ? "" : " (en)"}
                  </option>
                ))}
              </select>
            </label>
            <button onClick={signOut} className="text-xs underline text-muted-foreground hover:text-foreground">
              {t.sign_out}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        {noRole ? (
          <p className="rounded-sm border border-dashed border-border p-6 text-sm italic" style={{ color: "var(--rust)" }}>
            {t.no_role}
          </p>
        ) : openId ? (
          <StoryDetail t={t} lang={lang} id={openId} onBack={() => setOpenId(null)} />
        ) : (
          <Queue t={t} onOpen={setOpenId} />
        )}
      </main>
    </div>
  );
}